import { MutationTree, ActionTree, Module } from 'vuex';
import { RootState, SelectionsState, AsOf, AsOfType, AsOfRelativeUnit, Bucket } from './../types';
import utilsService from '@/services/utils.service';
import _ from 'lodash';

function checkForOldValues(profile: any, products: any, attrs: any, venues: any) {
  const warnings: string[] = [];
  const types = Object.keys(products);
  profile.types = profile.attributes ? profile.types.filter( (val: string) => {
    if (types.indexOf(val) === -1) {
      warnings.push(`We had to remove the product '${val}' from your profile`);
      return false;
    } else {
      return true;
    }
  }) : undefined;

  const attrIds = Object.keys(attrs);
  profile.attributes = profile.attributes ? profile.attributes.filter( (val: string) => {
    if (attrIds.indexOf(val) === -1) {
      warnings.push(`We had to remove the grouping attribute '${val}' from your profile`);
      return false;
    } else {
      return true;
    }
  }) : undefined;

  const sources = Object.keys(venues);
  profile.sources = profile.sources ? profile.sources.filter( (val: string) => {
    if (sources.indexOf(val) === -1) {
      warnings.push(`We had to remove the source '${val}' from your profile`);
      return false;
    } else {
      return true;
    }
  }) : undefined;

  return { sanitisedProfile: profile, warnings };
}

const state: SelectionsState = {
  selectedProduct: '', // TODO: remove
  selectedProducts: [],
  selectedDataSources: [],
  selectedAttributes: [],
  isLoadingFromProfile: false
};

const mutations: MutationTree <SelectionsState> = {
  setSelectedProducts(selectionsState, selectedProducts: string[] = []) {
    selectionsState.selectedProducts = selectedProducts;
  },
  setSelectedDataSources(selectionsState, selectedDataSources: string[] = []) {
    selectionsState.selectedDataSources = selectedDataSources;
  },
  setSelectedAttributes(selectionsState, selectedAttributes: string[] = []) {
    selectionsState.selectedAttributes = selectedAttributes;
  },
  setisLoadingFromProfile(selectionsState, isLoadingFromProfile = false) {
    selectionsState.isLoadingFromProfile = isLoadingFromProfile;
  }
};

const actions: ActionTree <SelectionsState, RootState> = {
  clearSelections(context) {
    context.commit('setSelectedProducts');

    context.commit('setSelectedDataSources');
    context.commit('setSelectedAttributes');

    context.commit('setResult');
    context.commit('setSelectedGraphIndex');

    context.commit('setAttrValues');

    context.commit('setCurrentProfileShowingOnGraph');
    const asOf: AsOf = { type: AsOfType.Relative, data: { count: 4, unit: AsOfRelativeUnit.Week } };
    context.commit('setAsOf', asOf);
    context.commit('setSelectedProfile');
    context.commit('setLoadedProfile');

    context.commit('resetAllBuckets');
  },
  async populateFromProfile(context, profile) {
    context.commit('setisLoadingFromProfile', true);

    // Determine if the asOf date is still valid
    let allowCalculation = true;
    if (profile.asOf && profile.asOf.type === 'static') {
      const profileTime = new Date(profile.asOf.data).getTime();
      const min = context.getters.getMinTime;
      const max = context.getters.getMaxTime;
      const asOfError = utilsService.isDateOutsideRange(profileTime, min, max);
      if (!!asOfError) {
        allowCalculation = false;
        context.commit('setModal', 'dateSelect');
        context.commit('setAsOfError', asOfError);
      }
    }

    // Regardless of whether asOf is valid, we want to set our selections to
    // match the profile, so that if the user changes the date, we already have
    // our profile selections loaded.
    context.commit('setResult');
    
    const opts = context.rootState.options;
    let { warnings, sanitisedProfile } = checkForOldValues(
      profile,
      opts.productsById,
      opts.attributesDisplayMap,
      opts.venuesDisplayMap
    );
    context.commit('setSelectedProducts', sanitisedProfile.types);
    context.commit('setSelectedDataSources', sanitisedProfile.sources);
    context.commit('setSelectedAttributes', sanitisedProfile.attributes);
    for (const attrId of Object.keys(sanitisedProfile.buckets || {})) {
      const bucket: Bucket = sanitisedProfile.buckets[attrId];
      context.commit('setSelectedBucketing', { bucket, id: attrId });
      if (bucket.type === 'custom') {
        context.commit('addCustomBucketToStoreIfUnique', bucket);
      }
    }

    if (warnings.length) {
      warnings.forEach((warning) => {
        context.commit('setToastInfo', warning);
      });
    }


    sanitisedProfile = utilsService.convertBucketToAttributeParams(sanitisedProfile, opts.options);
    context.commit('setAsOf', sanitisedProfile.asOf);

    // If the asOf date is valid, we can trigger a calculation.
    // If not, the user will be looking at the date picker modal, with
    // the option to cancel (abort loading the profile), or to pick a valid date
    if (allowCalculation) {
      await context.dispatch('calculateResults', {profile: sanitisedProfile, force: false});
      context.commit('setisLoadingFromProfile', false);
    }
  }
};

export const selections: Module<SelectionsState, RootState> = {
  state,
  mutations,
  actions
};
